import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { isSameDay, isBefore, format, subDays } from "date-fns";

import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import DataTable from "react-data-table-component";
import ButtonLoader from "./ButtonLoader";
import TableLoader from "./TableLoader";

import allocationService from "../services/allocationService";
import useAllocationReadyProjects from "../hooks/useAllocationReadyProjects";
import useBusyDates from "../hooks/useBusyDates";
import { formatDateToSydneyTimeZone, toApiDateTime } from "../utils/dateUtils";

const LabourerAllocationForm = ({ labourer, onAllocate, onCloseClicked }) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [dateRange, setDateRange] = useState([null, null]);

  const startDateIso = dateRange[0] ? toApiDateTime(format(dateRange[0], 'yyyy-MM-dd')) : null;
  const endDateIso = dateRange[1] ? toApiDateTime(format(dateRange[1], 'yyyy-MM-dd')) : null;

  const { data, isLoading: isLoadingProjects } = useAllocationReadyProjects({
    shouldFetch: !!startDateIso && !!endDateIso,
    labourerId: labourer._id,
    startDate: startDateIso,
    endDate: endDateIso,
    page,
    perPage
  });

  const projects = data?.projects;
  const projectCount = data?.count;

  const { data: busyDatesData, isLoading: isLoadingBusyDates } = useBusyDates({ labourerId: labourer._id });

  const busyDates = busyDatesData?.busyDates;

  const [selectedRows, setSelectedRows] = useState([]);
  const [isAllocating, setIsAllocating] = useState(false);

  const handlePageChange = page => {
    setPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const handleRowSelected = useCallback(state => {
    setSelectedRows(state.selectedRows);
  }, []);

  const projectColumns = [
    {
      name: 'Name',
      selector: row => row.name
    },
    {
      name: 'Status',
      selector: row => row.status
    },
    {
      name: 'Start Date',
      selector: row => formatDateToSydneyTimeZone(row.startDate)
    },
    {
      name: 'End Date',
      selector: row => formatDateToSydneyTimeZone(row.endDate)
    }
  ];

  const allocateLabourer = async () => {
    if (selectedRows.length === 0) return;
    if (!startDateIso || !endDateIso) return;

    const hasSelectedBusyDate = busyDates.some(busyDate =>
      !isBefore(busyDate, dateRange[0]) && isBefore(busyDate, dateRange[1])
    );

    if (hasSelectedBusyDate) {
      toast.error('Labourer is busy on selected dates');
      return;
    }

    setIsAllocating(true);

    try {
      await allocationService.allocateLabourer({
        labourerId: labourer._id,
        projectId: selectedRows[0]._id,
        startDate: startDateIso,
        endDate: endDateIso
      });

      toast.success('Labourer allocated', { icon: '🚀' });
      onAllocate();
    } catch (error) {
      console.log(error);
      const { message } = error?.response?.data;

      toast.error(message || 'An error occurred while allocating labourer');
    } finally {
      setIsAllocating(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-ase-blue w-auto md:w-[900px] min-h-[450px] pb-1">
      <div className="flex items-center justify-between text-lg bg-ase-blue text-white p-4 font-bold rounded-t-md">
        <p>{labourer?.user?.fullName} Allocation</p>

        <p
          onClick={onCloseClicked}
          className="font-bold cursor-pointer text-2xl"
        >
          X
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 px-4 py-4">
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2">
            <span className="text-xs font-semibold leading-0">Date range</span>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
              clearIcon={null}
              format="d/M/yyyy"
              className="h-[36px]"
              maxDetail="month"
              minDetail="year"
              required
              disabled={isLoadingBusyDates}
              calendarProps={{
                tileDisabled: ({ activeStartDate, date, view }) => {
                  return busyDates.some(busyDate => isSameDay(busyDate, date));
                }
              }}
            />
          </label>
        </div>

        <button
          onClick={allocateLabourer}
          className="ase-button-primary flex items-center justify-center grow h-[36px]"
        >
          {isAllocating ? <ButtonLoader /> : 'Allocate'}
        </button>
      </div>

      {!startDateIso && !endDateIso && (
        <div className="flex flex-grow items-center justify-center px-4 py-4 md:py-8 text-center">
          <p className="text-base">Select a date range to view available projects</p>
        </div>
      )}

      {startDateIso && endDateIso && (
        <DataTable
          columns={projectColumns}
          data={projects}
          striped
          highlightOnHover
          selectableRows
          pagination
          paginationServer
          progressPending={isLoadingProjects}
          progressComponent={<TableLoader />}
          paginationTotalRows={projectCount}
          paginationRowsPerPageOptions={[10, 20, 50]}
          paginationPerPage={perPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          pointerOnHover
          selectableRowsHighlight
          selectableRowsSingle
          onSelectedRowsChange={handleRowSelected}
          noDataComponent={
            <div className="flex items-center justify-center py-8">
              No projects available for allocation
            </div>
          }
        />
      )}
    </div>
  )
};

export default LabourerAllocationForm;
